/**
 * FC Quiz — Light Exposure Audit
 * Handles quiz navigation, scoring, and result routing.
 */
(function () {
  'use strict';

  class FCQuiz {
    constructor(container) {
      this.container = container;
      this.screens = container.querySelectorAll('.fc-quiz__screen');
      this.totalQuestions = parseInt(container.dataset.totalQuestions, 10) || 0;
      this.answers = {};
      this.currentScreen = 'intro';
      this.progressBar = container.querySelector('.fc-quiz__progress');
      this._advanceTimer = null;

      /* Score thresholds from data attributes */
      this.dayThresholdHigh = parseFloat(container.dataset.dayThresholdHigh) || 30;
      this.dayThresholdLow = parseFloat(container.dataset.dayThresholdLow) || 25;
      this.eveningThresholdHigh = parseFloat(container.dataset.eveningThresholdHigh) || 30;
      this.eveningThresholdLow = parseFloat(container.dataset.eveningThresholdLow) || 25;
      this.eveningDivisor = parseFloat(container.dataset.eveningDivisor) || 1.5;
      this.lowScoreThreshold = parseFloat(container.dataset.lowScoreThreshold) || 20;

      this.bindEvents();
      this.handleFooter();
      this.setHeightOffset();
    }

    /* ---- Event binding ---- */
    bindEvents() {
      /* Start button */
      this.container.querySelectorAll('[data-action="start"]').forEach(function (btn) {
        btn.addEventListener('click', this.onStart.bind(this));
      }.bind(this));

      /* Option buttons — auto-advance on click */
      this.container.querySelectorAll('.fc-quiz__option').forEach(function (btn) {
        btn.addEventListener('click', this.onOptionClick.bind(this));
      }.bind(this));

      /* Back buttons */
      this.container.querySelectorAll('[data-action="back"]').forEach(function (btn) {
        btn.addEventListener('click', this.onBack.bind(this));
      }.bind(this));

      /* Keyboard shortcuts */
      document.addEventListener('keydown', this.onKeyDown.bind(this));
    }

    /* ---- Keyboard navigation ---- */
    onKeyDown(e) {
      /* Only act on question screens */
      if (this.currentScreen.indexOf('question-') !== 0) return;

      var screen = this.container.querySelector('.fc-quiz__screen--active');
      if (!screen) return;

      var key = e.key.toUpperCase();
      var optionKeys = { A: 0, B: 1, C: 2, D: 3 };

      if (optionKeys.hasOwnProperty(key)) {
        /* Letter keys — select and auto-advance */
        e.preventDefault();
        var options = screen.querySelectorAll('.fc-quiz__option');
        if (options[optionKeys[key]]) {
          this.selectOption(options[optionKeys[key]], screen);
          this.scheduleAdvance(screen);
        }
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        /* Arrow keys — browse only, no auto-advance */
        e.preventDefault();
        var options = screen.querySelectorAll('.fc-quiz__option');
        var current = screen.querySelector('.fc-quiz__option--selected');
        var currentIdx = current ? Array.from(options).indexOf(current) : -1;

        if (e.key === 'ArrowDown') {
          var next = currentIdx < options.length - 1 ? currentIdx + 1 : 0;
        } else {
          var next = currentIdx > 0 ? currentIdx - 1 : options.length - 1;
        }
        this.selectOption(options[next], screen);
      } else if (e.key === 'Enter') {
        /* Enter — advance if an option is selected */
        e.preventDefault();
        var questionIndex = parseInt(screen.dataset.questionIndex, 10);
        if (this.answers.hasOwnProperty(questionIndex)) {
          this.advance(screen);
        }
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        var backBtn = screen.querySelector('[data-action="back"]');
        if (backBtn) backBtn.click();
      }
    }

    /* ---- Footer visibility ---- */
    handleFooter() {
      var hideFooter = this.container.dataset.hideFooter === 'true';
      var footer = document.querySelector('.footer');
      if (footer && hideFooter) {
        footer.style.display = 'none';
      }
    }

    /* ---- Height offset (header + announcement bar) ---- */
    setHeightOffset() {
      var headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0;
      var announcementBar = document.querySelector('.announcement-bar-section');
      var announcementHeight = announcementBar ? announcementBar.offsetHeight : 0;
      this.container.style.setProperty('--fc-quiz-offset', (headerHeight + announcementHeight) + 'px');
    }

    /* ---- Screen navigation ---- */
    showScreen(screenName) {
      var self = this;
      var current = this.container.querySelector('.fc-quiz__screen--active');
      var target = this.container.querySelector('[data-screen="' + screenName + '"]');
      if (!target || target === current) return;

      var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var exitDuration = prefersReduced ? 0 : 350;

      /* Exit current screen */
      if (current) {
        current.classList.remove('fc-quiz__screen--active');
        if (!prefersReduced) {
          current.classList.add('fc-quiz__screen--exiting');
        }
      }

      /* Enter new screen after exit completes */
      setTimeout(function () {
        if (current) {
          current.classList.remove('fc-quiz__screen--exiting');
        }

        target.classList.add('fc-quiz__screen--active');
        self.previousScreen = self.currentScreen;
        self.currentScreen = screenName;
        window.scrollTo({ top: 0, behavior: prefersReduced ? 'auto' : 'smooth' });

        /* Update persistent progress bar */
        self.updateProgress(screenName);

        /* Restore previous selection when navigating back to a question */
        if (screenName.indexOf('question-') === 0) {
          var qIdx = parseInt(screenName.replace('question-', ''), 10);
          self.restoreSelection(qIdx);
        }
      }, exitDuration);
    }

    /* ---- Persistent progress bar ---- */
    updateProgress(screenName) {
      if (!this.progressBar) return;

      var isQuestion = screenName.indexOf('question-') === 0;

      if (isQuestion) {
        var qIdx = parseInt(screenName.replace('question-', ''), 10);
        var qNumber = qIdx + 1;

        /* Show bar */
        var wasHidden = !this.progressBar.classList.contains('fc-quiz__progress--visible');
        this.progressBar.classList.add('fc-quiz__progress--visible');
        this.progressBar.setAttribute('aria-hidden', 'false');

        /* Update label and aria */
        var label = this.progressBar.querySelector('.fc-quiz__progress-label');
        if (label) label.textContent = qNumber + '/' + this.totalQuestions;

        var bar = this.progressBar.querySelector('.fc-quiz__progress-bar');
        if (bar) bar.setAttribute('aria-valuenow', qNumber);

        /* Update filled steps — defer on first show so the fill animates */
        var steps = this.progressBar.querySelectorAll('.fc-quiz__progress-step');
        var fillSteps = function () {
          steps.forEach(function (step) {
            var stepNum = parseInt(step.dataset.step, 10);
            if (stepNum <= qNumber) {
              step.classList.add('fc-quiz__progress-step--filled');
            } else {
              step.classList.remove('fc-quiz__progress-step--filled');
            }
          });
        };

        var comingFromResult = (this.previousScreen || '').indexOf('result') === 0;

        if (wasHidden && !comingFromResult) {
          /* First show (e.g. from intro) — animate the fill */
          requestAnimationFrame(function () {
            requestAnimationFrame(fillSteps);
          });
        } else if (wasHidden && comingFromResult) {
          /* Back from result — fill instantly, no animation */
          steps.forEach(function (step) {
            step.style.transition = 'none';
          });
          fillSteps();
          /* Restore transitions on next frame */
          requestAnimationFrame(function () {
            steps.forEach(function (step) {
              step.style.transition = '';
            });
          });
        } else {
          fillSteps();
        }
      } else {
        /* Hide bar immediately and reset steps */
        this.progressBar.classList.remove('fc-quiz__progress--visible');
        this.progressBar.setAttribute('aria-hidden', 'true');

        var steps = this.progressBar.querySelectorAll('.fc-quiz__progress-step');
        steps.forEach(function (step) {
          step.classList.remove('fc-quiz__progress-step--filled');
        });
      }
    }

    onStart() {
      if (this.totalQuestions > 0) {
        this.showScreen('question-0');
      }
    }

    /* ---- Advance to next question or result ---- */
    advance(screen) {
      var questionIndex = parseInt(screen.dataset.questionIndex, 10);

      if (questionIndex < this.totalQuestions - 1) {
        this.showScreen('question-' + (questionIndex + 1));
      } else {
        this.calculateAndShowResult();
      }
    }

    scheduleAdvance(screen) {
      var self = this;
      /* Clear any pending advance (e.g. user changed mind quickly) */
      if (this._advanceTimer) {
        clearTimeout(this._advanceTimer);
      }
      this._advanceTimer = setTimeout(function () {
        self._advanceTimer = null;
        self.advance(screen);
      }, 400);
    }

    onBack(e) {
      var screen = e.currentTarget.closest('.fc-quiz__screen');
      var screenName = screen.dataset.screen;

      /* Cancel any pending auto-advance if user hits back quickly */
      if (this._advanceTimer) {
        clearTimeout(this._advanceTimer);
        this._advanceTimer = null;
      }

      if (screenName && screenName.indexOf('question-') === 0) {
        var questionIndex = parseInt(screen.dataset.questionIndex, 10);
        if (questionIndex > 0) {
          this.showScreen('question-' + (questionIndex - 1));
        } else {
          this.showScreen('intro');
        }
      } else if (screenName && screenName.indexOf('result') === 0) {
        /* Back from result → last question */
        this.showScreen('question-' + (this.totalQuestions - 1));
      }
    }

    /* ---- Option selection (visual + store, no advance) ---- */
    selectOption(option, screen) {
      var questionIndex = parseInt(screen.dataset.questionIndex, 10);

      /* Deselect siblings */
      screen.querySelectorAll('.fc-quiz__option').forEach(function (opt) {
        opt.classList.remove('fc-quiz__option--selected');
        opt.setAttribute('aria-checked', 'false');
      });

      /* Select this one */
      option.classList.add('fc-quiz__option--selected');
      option.setAttribute('aria-checked', 'true');

      /* Store answer */
      var options = Array.from(screen.querySelectorAll('.fc-quiz__option'));
      this.answers[questionIndex] = {
        points: parseInt(option.dataset.points, 10) || 0,
        category: screen.dataset.category || 'day',
        optionIndex: options.indexOf(option)
      };

      /* Remove validation highlight if present */
      screen.querySelectorAll('.fc-quiz__options').forEach(function (opts) {
        opts.style.outline = '';
      });
    }

    /* ---- Click on option — select + auto-advance ---- */
    onOptionClick(e) {
      var option = e.currentTarget;
      var screen = option.closest('.fc-quiz__screen');

      this.selectOption(option, screen);
      this.scheduleAdvance(screen);
    }

    /* ---- Restore selections when navigating back ---- */
    restoreSelection(questionIndex) {
      if (!this.answers.hasOwnProperty(questionIndex)) return;

      var screen = this.container.querySelector('[data-question-index="' + questionIndex + '"]');
      if (!screen) return;

      var savedIndex = this.answers[questionIndex].optionIndex;
      var options = screen.querySelectorAll('.fc-quiz__option');

      options.forEach(function (opt, i) {
        if (i === savedIndex) {
          opt.classList.add('fc-quiz__option--selected');
          opt.setAttribute('aria-checked', 'true');
        } else {
          opt.classList.remove('fc-quiz__option--selected');
          opt.setAttribute('aria-checked', 'false');
        }
      });
    }

    /* ---- Score calculation ---- */
    calculateAndShowResult() {
      var dayRaw = 0;
      var eveningRaw = 0;

      for (var key in this.answers) {
        if (!this.answers.hasOwnProperty(key)) continue;
        var answer = this.answers[key];
        var pts = answer.points;
        var cat = answer.category;

        if (cat === 'day') {
          dayRaw += pts;
        } else if (cat === 'evening') {
          eveningRaw += pts;
        } else if (cat === 'both') {
          dayRaw += pts * 0.5;
          eveningRaw += pts * 0.5;
        }
      }

      /* Apply evening divisor */
      if (this.eveningDivisor > 0 && this.eveningDivisor !== 1) {
        eveningRaw = eveningRaw / this.eveningDivisor;
      }

      /* Cap at 50 */
      var dayScore = Math.min(Math.round(dayRaw), 50);
      var eveningScore = Math.min(Math.round(eveningRaw), 50);
      var totalScore = Math.min(dayScore + eveningScore, 100);

      /* Determine scenario */
      var scenario;
      if (totalScore < this.lowScoreThreshold) {
        scenario = 'result-low';
      } else if (dayScore >= this.dayThresholdHigh && eveningScore < this.eveningThresholdLow) {
        scenario = 'result-focus';
      } else if (eveningScore >= this.eveningThresholdHigh && dayScore < this.dayThresholdLow) {
        scenario = 'result-unwind';
      } else if (dayScore >= this.dayThresholdLow && eveningScore >= this.eveningThresholdLow) {
        scenario = 'result-bundle';
      } else {
        /* Fallback: pick the higher sub-score */
        if (dayScore > eveningScore) {
          scenario = 'result-focus';
        } else if (eveningScore > dayScore) {
          scenario = 'result-unwind';
        } else {
          scenario = 'result-bundle';
        }
      }

      /* Populate score values in the result screen */
      var resultScreen = this.container.querySelector('[data-screen="' + scenario + '"]');
      if (resultScreen) {
        resultScreen.querySelectorAll('[data-score-total]').forEach(function (el) {
          el.textContent = totalScore;
        });
        resultScreen.querySelectorAll('[data-score-day]').forEach(function (el) {
          el.textContent = dayScore;
        });
        resultScreen.querySelectorAll('[data-score-evening]').forEach(function (el) {
          el.textContent = eveningScore;
        });
      }

      this.showScreen(scenario);
    }

  }

  /* ---- Initialise ---- */
  function init() {
    document.querySelectorAll('.fc-quiz').forEach(function (el) {
      new FCQuiz(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
